/* @jsxImportSource react */
'use client';

import React from 'react';

import { useObservability } from '@opendoor/observability/slim';

import { type LinkRenderProps, useLinkRenderer } from './link-provider';
import { type StripUndesiredProps } from './strip-undesired-props';

type ContainerElement =
  | 'div'
  | 'aside'
  | 'header'
  | 'footer'
  | 'nav'
  | 'main'
  | 'section'
  | 'article';

type SemanticContainerProps = Omit<
  StripUndesiredProps<React.HTMLAttributes<HTMLElement>>,
  'onClick'
> & {
  /**
   * Semantic element to render. Mutually exclusive with `onClick` and `href`.
   * @default "div"
   */
  as?: ContainerElement;
  children?: React.ReactNode;
  onClick?: never;
  href?: never;
};

type ButtonContainerProps = Omit<
  StripUndesiredProps<React.ButtonHTMLAttributes<HTMLButtonElement>>,
  'aria-label'
> & {
  /**
   * Click handler. Presence of `onClick` renders the container as
   * `<button type="button">` and requires `aria-label`. Mutually exclusive
   * with `as` and `href`.
   */
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  /** Required when the container is interactive — names the action for assistive tech. */
  'aria-label': string;
  children?: React.ReactNode;
  as?: never;
  href?: never;
};

type AnchorContainerProps = Omit<StripUndesiredProps<LinkRenderProps>, 'aria-label' | 'href'> & {
  /**
   * Navigation target. Presence of `href` renders the container as `<a>` via
   * the configured `LinkProvider` (so framework routing works) and requires
   * `aria-label`. Mutually exclusive with `as` and `onClick`-only mode.
   */
  href: string;
  /** Required when the container is interactive — names the destination for assistive tech. */
  'aria-label': string;
  /** When `true`, opens in a new tab with `rel="noopener noreferrer"`. */
  external?: boolean;
  children?: React.ReactNode;
  as?: never;
};

/**
 * The three structural container variants, before analytics props are layered
 * on. `Grid` uses this directly (it has no analytics surface); `Box` extends it
 * with `AnalyticsProps`, and `Container` itself adds `analyticsName` so the
 * click-event wiring below can read it.
 */
export type ContainerVariantProps =
  | SemanticContainerProps
  | ButtonContainerProps
  | AnchorContainerProps;

// `analyticsName` drives the click-event wiring in `Container` below: an
// interactive container fires `linkClickEvent` (href) or `buttonClickEvent`
// (onClick), mirroring `Link`/`Button`. Its doc comment is kept identical to
// `AnalyticsProps.analyticsName` on purpose: the registry generator prefers this
// declaration, so anything else here would change `Box`'s generated description.
export type ContainerProps = ContainerVariantProps & {
  /**
   * Names this element in journey-service events. Convention:
   * `{appName}-{componentName}`. Optional — when unset, no analytics events are
   * emitted (every dispatch is guarded by `if (analyticsName)`).
   */
  analyticsName?: string;
};

export const Container = React.forwardRef<HTMLElement, ContainerProps>((props, ref) => {
  const renderLink = useLinkRenderer();
  const { trackJourneyEvent } = useObservability();

  if (props.href !== undefined) {
    const { children, external, as: _as, href, analyticsName, onClick, ...rest } = props;
    const externalProps = external ? { target: '_blank', rel: 'noopener noreferrer' } : {};
    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (analyticsName) {
        trackJourneyEvent('platform', 'linkClickEvent', { targetName: analyticsName });
      }
      onClick?.(event);
    };
    // Call the configured renderer (see RenderLink) rather than rendering a
    // context-provided component, so the anchor's element type stays static.
    return renderLink({
      // eslint-disable-next-line typescript/no-unsafe-type-assertion -- forwardRef gives Ref<HTMLElement>; this branch only ever renders an anchor, so narrowing the ref is sound
      ref: ref as React.Ref<HTMLAnchorElement>,
      href,
      to: href,
      ...externalProps,
      ...rest,
      onClick: handleClick,
      children,
    });
  }

  if (props.onClick) {
    const { children, as: _as, href: _href, analyticsName, onClick, ...rest } = props;
    // eslint-disable-next-line typescript/no-unsafe-type-assertion -- forwardRef gives Ref<HTMLElement>; this branch renders a <button>, so narrowing the ref is sound
    const buttonRef = ref as React.Ref<HTMLButtonElement>;
    // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- the handler closes over per-render props (analyticsName/onClick); a memoized version can't live in this branch (conditional hook) and a top-level one can't be typed across Container's discriminated union without an unsafe cast. Container is a leaf primitive, so a fresh handler per render is fine.
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (analyticsName) {
        trackJourneyEvent('platform', 'buttonClickEvent', { targetName: analyticsName });
      }
      onClick(event);
    };
    return (
      // eslint-disable-next-line atoms/no-raw-elements -- Box/Grid render a <button> here when given onClick; this is the layout primitives' button implementation
      <button type="button" ref={buttonRef} onClick={handleClick} {...rest}>
        {children}
      </button>
    );
  }

  const {
    as: Component = 'div',
    children,
    onClick: _onClick,
    href: _href,
    analyticsName: _analyticsName,
    ...rest
  } = props;
  // eslint-disable-next-line typescript/no-unsafe-type-assertion -- forwardRef gives Ref<HTMLElement>; the rendered semantic element extends HTMLElement, so narrowing the ref is sound
  const componentRef = ref as React.Ref<HTMLDivElement>;
  return (
    <Component ref={componentRef} {...rest}>
      {children}
    </Component>
  );
});

Container.displayName = 'Container';
